import { useState, useCallback } from "react";
import { useGetList } from "react-admin";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Switch,
  TextField,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";
import {
  CalendarMonth,
  CheckCircle,
  Cancel,
  Add,
  EditOutlined,
  Close,
  PeopleOutline,
  Description,
  DeleteOutline,
  ExpandMore,
} from "@mui/icons-material";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { useAppTheme } from "@/hooks/useAppTheme";
import { useEndorsements, formatValidPeriod } from "../hooks/useEndorsements";
import { useEndorsementAuditLog } from "../hooks/useEndorsementAuditLog";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import {
  ALL_QUALIFYING_CONDITIONS,
  type AdminChangeEntry,
  type AmendmentEntry,
  type TieredDeductibleTier,
  type QualifyingCondition,
  type DomesticReimbursementEntry,
  type AuditRecord,
  type DeductibleHistoryItem,
  type EndorsementType,
  type EndorsementListItem,
} from "@/types";
import { getDetailDescription } from "../endorsements/endorsementDescriptions";
import { getFieldSchema } from "../endorsements/endorsementFieldSchemas";
import { formatDeductibleDollars } from "@/resources/claimants/hooks/useDeductibleHistory";
import { EditableCodeChipSection } from "./EditableCodeChipSection";
import { ClaimIdPicker } from "./ClaimIdPicker";
import { DeductibleModificationDialog } from "@/resources/claimants/components/DeductibleModificationDialog";

interface EndorsementDetailPanelProps {
  policyId: string;
  endorsementId: string;
  userCanEdit: boolean;
  /** When true, endorsements follow the policy status — skip date-based active check */
  policyIsActive?: boolean;
  /** Called when user clicks Edit — parent transitions drawer to edit mode */
  onEdit?: (endorsementId: string) => void;
  /** Called when user toggles active/inactive (Workflow 5) */
  onToggleActive?: (endorsementId: string) => void;
  /** Called when user updates ICD/CPT codes (Workflow 7) */
  onUpdateCodes?: (endorsementId: string, codeType: "icd" | "cpt", codes: string[]) => void;
  /** Called when user updates qualifying conditions (Workflow 8) */
  onUpdateConditions?: (endorsementId: string, conditions: QualifyingCondition[]) => void;
  /** Called when user updates admin change entries */
  onUpdateAdminChangeEntries?: (endorsementId: string, entries: AdminChangeEntry[]) => void;
  /** Called when user updates amendment entries */
  onUpdateAmendmentEntries?: (endorsementId: string, entries: AmendmentEntry[]) => void;
  /** Called when user updates domestic reimbursement entries */
  onUpdateDomesticReimbursementEntries?: (endorsementId: string, entries: DomesticReimbursementEntry[]) => void;
  /** Called when user updates tiered deductible tiers */
  onUpdateTiers?: (endorsementId: string, tiers: TieredDeductibleTier[]) => void;
  /** Called when user updates any string-array junction field (excluded_icd, ndc_codes, etc.) */
  onUpdateStringArray?: (endorsementId: string, field: string, values: string[]) => void;
  /** Called when user deletes the endorsement */
  onDelete?: (endorsementId: string) => void;
  /** Whether the PDF split-pane is currently visible */
  pdfOpen?: boolean;
  /** Toggle PDF split-pane alongside endorsement detail */
  onTogglePdf?: () => void;
}

// ─────────────────────────────────────────────────────────────
// Inline POS code chips for DRA entry rows
// ─────────────────────────────────────────────────────────────

const POS_CHIP_COLORS = { bg: "#fff7ed", color: "#9a3412" };

const InlinePosCodes = ({
  codes,
  onUpdate,
  canEdit,
}: {
  codes: string[];
  onUpdate: (codes: string[]) => void;
  canEdit: boolean;
}) => {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim().toUpperCase();
    if (!trimmed || codes.includes(trimmed)) {
      setInput("");
      return;
    }
    onUpdate([...codes, trimmed]);
    setInput("");
  };

  const handleRemove = (code: string) => {
    onUpdate(codes.filter((c) => c !== code));
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, alignItems: "center", minHeight: 22 }}>
      {codes.map((code) => (
        <Chip
          key={code}
          label={code}
          size="small"
          onDelete={canEdit ? () => handleRemove(code) : undefined}
          sx={{
            height: 20,
            bgcolor: POS_CHIP_COLORS.bg,
            color: POS_CHIP_COLORS.color,
            fontFamily: "monospace",
            fontSize: "0.7rem",
            fontWeight: 500,
            "& .MuiChip-deleteIcon": { color: POS_CHIP_COLORS.color, fontSize: 12 },
            "& .MuiChip-label": { px: 0.5 },
          }}
        />
      ))}
      {canEdit && (
        <TextField
          size="small"
          placeholder="+"
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, 4))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          }}
          sx={{
            width: 44,
            "& .MuiInputBase-root": { height: 22, fontSize: "0.7rem", fontFamily: "monospace" },
            "& .MuiInputBase-input": { py: 0, px: 0.5 },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: POS_CHIP_COLORS.color },
          }}
        />
      )}
      {codes.length === 0 && !canEdit && (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.7rem" }}>
          —
        </Typography>
      )}
    </Box>
  );
};

/**
 * Renders endorsement detail content inside the SideDrawer.
 * Shows status, valid period, configuration fields, qualifying codes/conditions,
 * admin change entries, and amendment entries with inline add/remove capability.
 */
export const EndorsementDetailPanel = ({
  policyId,
  endorsementId,
  userCanEdit,
  policyIsActive = false,
  onEdit,
  onToggleActive,
  onUpdateCodes,
  onUpdateConditions,
  onUpdateAdminChangeEntries,
  onUpdateAmendmentEntries,
  onUpdateDomesticReimbursementEntries,
  onUpdateTiers,
  onUpdateStringArray,
  onDelete,
  pdfOpen,
  onTogglePdf,
}: EndorsementDetailPanelProps) => {
  const { theme, isDarkMode } = useAppTheme();
  const navigate = useNavigate();
  const { getById } = useEndorsements(policyId, policyIsActive);
  const { records: auditRecords, isLoading: auditLoading } = useEndorsementAuditLog(endorsementId);

  const endorsement = getById(endorsementId);

  // Workflow 8 — Conditions editing state
  const [editingConditions, setEditingConditions] = useState(false);

  // Admin change entries editing state
  const [editingAdminEntries, setEditingAdminEntries] = useState(false);
  const [newAdminChangeType, setNewAdminChangeType] = useState("");
  const [newAdminChangeDate, setNewAdminChangeDate] = useState("");

  // Amendment entries editing state
  const [editingAmendmentEntries, setEditingAmendmentEntries] = useState(false);
  const [newAmendmentNumber, setNewAmendmentNumber] = useState("");
  const [newAmendmentDate, setNewAmendmentDate] = useState("");

  // Tiered deductible tiers editing state
  const [editingTiers, setEditingTiers] = useState(false);
  const [newTierDeductible, setNewTierDeductible] = useState("");
  const [newTierAgeStart, setNewTierAgeStart] = useState("");
  const [newTierAgeEnd, setNewTierAgeEnd] = useState("");
  const [newTierReimbPct, setNewTierReimbPct] = useState("");

  // Domestic reimbursement entries editing state
  const [editingDraEntries, setEditingDraEntries] = useState(false);
  const [newDraReason, setNewDraReason] = useState("");
  const [newDraAdjustmentPct, setNewDraAdjustmentPct] = useState("");
  const [newDraPosCodes, setNewDraPosCodes] = useState<string[]>([]);

  /** Compute the next amendment number: max(existing) + 1, or 1 if none */
  const getNextAmendmentNumber = useCallback((): string => {
    const entries = endorsement?.amendment_entries ?? [];
    if (entries.length === 0) return "1";
    const max = Math.max(...entries.map((e) => e.amendment_number));
    return String(max + 1);
  }, [endorsement?.amendment_entries]);

  /** Check whether the entered amendment number already exists */
  const isAmendmentNumberDuplicate = (() => {
    const parsed = parseInt(newAmendmentNumber, 10);
    if (isNaN(parsed)) return false;
    return (endorsement?.amendment_entries ?? []).some((e) => e.amendment_number === parsed);
  })();

  /** Compute the next tier_order: max(existing) + 1, or 1 if none */
  const getNextTierOrder = useCallback((): number => {
    const tiers = endorsement?.tiers ?? [];
    if (tiers.length === 0) return 1;
    return Math.max(...tiers.map((t) => t.tier_order)) + 1;
  }, [endorsement?.tiers]);

  // Delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Admin change entry confirmation dialog
  const [adminChangeConfirmOpen, setAdminChangeConfirmOpen] = useState(false);

  // Affected claimants — deductible_modification type only
  const isDeductibleMod = endorsement?.type === "deductible_modification";
  const { data: affectedClaimants = [], refetch: refetchAffectedClaimants } = useGetList<DeductibleHistoryItem>(
    `endorsements/${endorsementId}/claimant-modifications`,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "trigger_date", order: "DESC" },
    },
    { enabled: !!endorsementId && isDeductibleMod }
  );

  // Dialog state for adding claimant deductible modifications
  const [deductibleModDialogOpen, setDeductibleModDialogOpen] = useState(false);

  if (!endorsement) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.disabled">
          Endorsement not found.
        </Typography>
      </Box>
    );
  }

  const statusColor = endorsement.is_currently_active ? "success" : "default";
  const statusLabel = endorsement.is_currently_active ? "Active" : "Inactive";

  // Check if today falls outside the valid period — if so, toggling is_enabled is meaningless
  const today = new Date().toISOString().slice(0, 10);
  const isOutsideValidPeriod =
    (!!endorsement.valid_period_start && today < endorsement.valid_period_start) ||
    (!!endorsement.valid_period_end && today > endorsement.valid_period_end);

  const handleAddAdminChangeEntry = () => {
    if (!newAdminChangeType.trim() || !newAdminChangeDate) return;
    const existing = endorsement?.admin_change_entries ?? [];
    const newEntry: AdminChangeEntry = {
      id: crypto.randomUUID(),
      admin_change_type: newAdminChangeType.trim(),
      effective_date: newAdminChangeDate,
    };
    onUpdateAdminChangeEntries?.(endorsementId, [...existing, newEntry]);
    setNewAdminChangeType("");
    setNewAdminChangeDate("");
    setAdminChangeConfirmOpen(true);
  };

  const handleDeleteAdminChangeEntry = (entryId: string) => {
    const existing = endorsement?.admin_change_entries ?? [];
    onUpdateAdminChangeEntries?.(
      endorsementId,
      existing.filter((e) => e.id !== entryId)
    );
  };

  const handleAddAmendmentEntry = () => {
    if (!newAmendmentNumber || !newAmendmentDate) return;
    const existing = endorsement?.amendment_entries ?? [];
    const parsedNumber = parseInt(newAmendmentNumber, 10);
    if (isNaN(parsedNumber)) return;
    const newEntry: AmendmentEntry = {
      id: crypto.randomUUID(),
      amendment_number: parsedNumber,
      effective_date: newAmendmentDate,
    };
    const updatedEntries = [...existing, newEntry];
    onUpdateAmendmentEntries?.(endorsementId, updatedEntries);
    // Auto-fill next number based on updated entries
    const nextNum = Math.max(...updatedEntries.map((e) => e.amendment_number)) + 1;
    setNewAmendmentNumber(String(nextNum));
    setNewAmendmentDate("");
  };

  const handleDeleteAmendmentEntry = (entryId: string) => {
    const existing = endorsement?.amendment_entries ?? [];
    onUpdateAmendmentEntries?.(
      endorsementId,
      existing.filter((e) => e.id !== entryId)
    );
  };

  const handleAddTier = () => {
    const deductible = parseFloat(newTierDeductible);
    const ageStart = parseInt(newTierAgeStart, 10);
    const ageEnd = parseInt(newTierAgeEnd, 10);
    const reimbPct = parseFloat(newTierReimbPct);
    if (isNaN(deductible) || isNaN(ageStart) || isNaN(ageEnd) || isNaN(reimbPct)) return;
    const existing = endorsement?.tiers ?? [];
    const newTier: TieredDeductibleTier = {
      id: crypto.randomUUID(),
      tier_order: getNextTierOrder(),
      deductible,
      age_range_start: ageStart,
      age_range_end: ageEnd,
      reimbursement_pct: reimbPct / 100, // UI shows %, API stores decimal
    };
    onUpdateTiers?.(endorsementId, [...existing, newTier]);
    setNewTierDeductible("");
    setNewTierAgeStart("");
    setNewTierAgeEnd("");
    setNewTierReimbPct("");
  };

  const handleDeleteTier = (tierId: string) => {
    const existing = endorsement?.tiers ?? [];
    const filtered = existing.filter((t) => t.id !== tierId);
    // Re-order remaining tiers sequentially
    const reordered = filtered.map((t, i) => ({ ...t, tier_order: i + 1 }));
    onUpdateTiers?.(endorsementId, reordered);
  };

  const handleAddDraEntry = () => {
    if (!newDraReason.trim()) return;
    const pctNum = parseFloat(newDraAdjustmentPct);
    if (isNaN(pctNum)) return;

    const existing = endorsement?.domestic_reimbursement_entries ?? [];
    const newEntry: DomesticReimbursementEntry = {
      id: crypto.randomUUID(),
      reason: newDraReason.trim(),
      adjustment_percentage: parseFloat((pctNum / 100).toPrecision(10)),
      ...(newDraPosCodes.length > 0 ? { pos_codes: newDraPosCodes } : {}),
    };

    onUpdateDomesticReimbursementEntries?.(endorsementId, [...existing, newEntry]);
    setNewDraReason("");
    setNewDraAdjustmentPct("");
    setNewDraPosCodes([]);
  };

  const handleDeleteDraEntry = (entryId: string) => {
    const existing = endorsement?.domestic_reimbursement_entries ?? [];
    onUpdateDomesticReimbursementEntries?.(
      endorsementId,
      existing.filter((e) => e.id !== entryId)
    );
  };

  /** Update POS codes for a specific DRA entry */
  const handleUpdateEntryPosCodes = (entryId: string, posCodes: string[]) => {
    const existing = endorsement?.domestic_reimbursement_entries ?? [];
    const updated = existing.map((e) => (e.id === entryId ? { ...e, pos_codes: posCodes } : e));
    onUpdateDomesticReimbursementEntries?.(endorsementId, updated);
  };

  const handleToggleCondition = (condition: QualifyingCondition) => {
    const existing = endorsement.qualifying_conditions || [];
    const updated = existing.includes(condition) ? existing.filter((c) => c !== condition) : [...existing, condition];
    onUpdateConditions?.(endorsementId, updated);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
        {/* Meta row: status + version + toggle + edit button */}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <StatusChip label={statusLabel} color={statusColor} />

          {/* Workflow 5 — Active/Inactive toggle */}
          {userCanEdit && onToggleActive && (
            <Tooltip
              title={
                isOutsideValidPeriod
                  ? "Cannot toggle — today is outside the valid period"
                  : endorsement.is_currently_active
                    ? "Deactivate endorsement"
                    : "Activate endorsement"
              }
            >
              <span style={{ marginLeft: "auto" }}>
                {/* span wrapper required for Tooltip on disabled elements */}
                <Switch
                  size="small"
                  checked={endorsement.is_currently_active}
                  onChange={() => onToggleActive(endorsementId)}
                  disabled={isOutsideValidPeriod}
                />
              </span>
            </Tooltip>
          )}

          {userCanEdit && onEdit && (
            <Tooltip title="Edit endorsement">
              <IconButton
                size="small"
                onClick={() => onEdit(endorsementId)}
                sx={{ ...(!onToggleActive && { ml: "auto" }) }}
              >
                <EditOutlined sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          {userCanEdit && onDelete && (
            <Tooltip title="Delete endorsement">
              <IconButton
                size="small"
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
              >
                <DeleteOutline sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Valid period pill */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            py: 1,
            bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50],
            borderRadius: 1.5,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CalendarMonth sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Valid Period
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ ml: "auto" }}>
            {formatValidPeriod(endorsement.valid_period_start, endorsement.valid_period_end)}
          </Typography>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            lineHeight: 1.6,
            fontSize: "0.8125rem",
          }}
        >
          {getDetailDescription(endorsement.type)}
        </Typography>

        {/* Configuration section */}
        <SectionLabel>Configuration</SectionLabel>
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            px: 1.5,
          }}
        >
          <ConfigFields config={endorsement.config} endorsementType={endorsement.type} />
        </Box>

        {/* Qualifying conditions (deductible_modification type) — Workflow 8 */}
        {endorsement.type === "deductible_modification" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.75 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
                Qualifying Conditions
              </Typography>
              {userCanEdit && onUpdateConditions && (
                <Typography
                  variant="caption"
                  onClick={() => setEditingConditions((prev) => !prev)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {editingConditions ? "Done" : "Edit"}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                p: 1.5,
              }}
            >
              {editingConditions ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {ALL_QUALIFYING_CONDITIONS.map((condition) => {
                    const isSelected = endorsement.qualifying_conditions?.includes(condition) ?? false;
                    return (
                      <Chip
                        key={condition}
                        label={formatToTitleCase(condition)}
                        size="small"
                        onClick={() => handleToggleCondition(condition)}
                        sx={{
                          bgcolor: isSelected
                            ? "#fef3c7"
                            : isDarkMode
                              ? theme.palette.grey[800]
                              : theme.palette.grey[100],
                          color: isSelected ? "#92400e" : "text.secondary",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          "&:hover": { opacity: 0.8 },
                        }}
                      />
                    );
                  })}
                </Box>
              ) : (endorsement.qualifying_conditions || []).length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {(endorsement.qualifying_conditions || []).map((condition) => (
                    <Box
                      key={condition}
                      component="span"
                      sx={{
                        px: 1,
                        py: 0.25,
                        bgcolor: "#fef3c7",
                        color: "#92400e",
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {formatToTitleCase(condition)}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  No qualifying conditions selected.{" "}
                  {userCanEdit && onUpdateConditions && (
                    <Typography
                      component="span"
                      variant="caption"
                      onClick={() => setEditingConditions(true)}
                      sx={{ color: "primary.main", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    >
                      Add conditions
                    </Typography>
                  )}
                </Typography>
              )}
            </Box>
          </>
        )}

        {/* Excluded ICD codes (exclusions, plan_mirroring, clinical_trials) — VARCHAR(8) */}
        {(["exclusions", "plan_mirroring", "clinical_trials"] as EndorsementType[]).includes(endorsement.type) && (
          <EditableCodeChipSection
            label="Excluded ICD Codes"
            codes={endorsement.excluded_icd ?? []}
            color="icd"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "excluded_icd", values)}
            placeholder="e.g. E11.9"
            maxLength={8}
          />
        )}

        {/* Excluded CPT codes (exclusions, plan_mirroring, transplant_coverage, reference_based_pricing) — CHAR(5) */}
        {(
          ["exclusions", "plan_mirroring", "transplant_coverage", "reference_based_pricing"] as EndorsementType[]
        ).includes(endorsement.type) && (
          <EditableCodeChipSection
            label="Excluded CPT Codes"
            codes={endorsement.excluded_cpt ?? []}
            color="cpt"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "excluded_cpt", values)}
            placeholder="e.g. 99213"
            maxLength={5}
          />
        )}

        {/* NDC codes (specialty_drug) */}
        {endorsement.type === "specialty_drug" && (
          <EditableCodeChipSection
            label="NDC Codes"
            codes={endorsement.ndc_codes ?? []}
            color="ndc"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "ndc_codes", values)}
            placeholder="Enter NDC code..."
          />
        )}

        {/* Domestic reimbursement entries (domestic_reimbursement_adjustments) */}
        {(endorsement.type === "domestic_reimbursement_adjustments" ||
          (endorsement.domestic_reimbursement_entries && endorsement.domestic_reimbursement_entries.length > 0)) && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.75 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
                Reimbursement Entries
              </Typography>
              {userCanEdit && onUpdateDomesticReimbursementEntries && (
                <Typography
                  variant="caption"
                  onClick={() => setEditingDraEntries((prev) => !prev)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {editingDraEntries ? "Done" : "Edit"}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
              }}
            >
              {/* Header row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: editingDraEntries ? "1fr 0.5fr 1fr 32px" : "1fr 0.5fr 1fr",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50],
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                {["Reason", "Adj. %", "POS Codes"].map((h) => (
                  <Typography key={h} variant="caption" fontWeight={700} color="text.secondary">
                    {h}
                  </Typography>
                ))}
                {editingDraEntries && <Box />}
              </Box>
              {(endorsement.domestic_reimbursement_entries ?? []).length === 0 && !editingDraEntries ? (
                <Box sx={{ p: 1.5 }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No reimbursement entries recorded.
                  </Typography>
                </Box>
              ) : (
                <>
                  {(endorsement.domestic_reimbursement_entries ?? []).map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: editingDraEntries ? "1fr 0.5fr 1fr 32px" : "1fr 0.5fr 1fr",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        alignItems: "center",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        "&:last-child": { borderBottom: editingDraEntries ? undefined : "none" },
                      }}
                    >
                      <Typography variant="body2">{entry.reason}</Typography>
                      <Typography variant="body2">
                        {parseFloat((entry.adjustment_percentage * 100).toPrecision(10))}%
                      </Typography>
                      <InlinePosCodes
                        codes={entry.pos_codes ?? []}
                        onUpdate={(codes) => handleUpdateEntryPosCodes(entry.id, codes)}
                        canEdit={editingDraEntries && userCanEdit && !!onUpdateDomesticReimbursementEntries}
                      />
                      {editingDraEntries && (
                        <IconButton size="small" onClick={() => handleDeleteDraEntry(entry.id)}>
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </>
              )}
              {editingDraEntries && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 0.5fr 1fr 32px",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    alignItems: "center",
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Reason..."
                    value={newDraReason}
                    onChange={(e) => setNewDraReason(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <TextField
                    size="small"
                    placeholder="%"
                    type="number"
                    value={newDraAdjustmentPct}
                    onChange={(e) => setNewDraAdjustmentPct(e.target.value)}
                    sx={{
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <InlinePosCodes codes={newDraPosCodes} onUpdate={setNewDraPosCodes} canEdit />
                  <IconButton
                    size="small"
                    onClick={handleAddDraEntry}
                    disabled={!newDraReason.trim() || !newDraAdjustmentPct}
                    sx={{ color: "primary.main" }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Excluded countries (travel_medical) — ISO alpha-2 codes, CHAR(2) */}
        {endorsement.type === "travel_medical" && (
          <EditableCodeChipSection
            label="Excluded Countries"
            codes={endorsement.excluded_countries ?? []}
            color="country"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "excluded_countries", values)}
            placeholder="e.g. US, GB..."
            maxLength={2}
          />
        )}

        {/* Excluded facilities (reference_based_pricing) */}
        {endorsement.type === "reference_based_pricing" && (
          <EditableCodeChipSection
            label="Excluded Facilities"
            codes={endorsement.excluded_facilities ?? []}
            color="facility"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "excluded_facilities", values)}
            placeholder="Enter facility type..."
          />
        )}

        {/* Credited claims (provider_dispute_credit) — picks from actual stoploss claims on this policy */}
        {endorsement.type === "provider_dispute_credit" && (
          <ClaimIdPicker
            policyId={policyId}
            claimIds={endorsement.credited_claim_ids ?? []}
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "credited_claim_ids", values)}
          />
        )}

        {/* Trigger types (contingent_terminal_liability) */}
        {endorsement.type === "contingent_terminal_liability" && (
          <EditableCodeChipSection
            label="Trigger Types"
            codes={endorsement.trigger_types ?? []}
            color="trigger"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "trigger_types", values)}
            placeholder="Enter trigger type..."
          />
        )}

        {/* Clinical trial phases */}
        {endorsement.type === "clinical_trials" && (
          <EditableCodeChipSection
            label="Covered Phases"
            codes={endorsement.phases ?? []}
            color="phase"
            canEdit={userCanEdit && !!onUpdateStringArray}
            onUpdate={(values) => onUpdateStringArray?.(endorsementId, "phases", values)}
            placeholder="Enter phase..."
          />
        )}

        {/* Tiered deductible tiers */}
        {(endorsement.type === "tiered_deductible" || (endorsement.tiers && endorsement.tiers.length > 0)) && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.75 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
                Deductible Tiers
              </Typography>
              {userCanEdit && onUpdateTiers && (
                <Typography
                  variant="caption"
                  onClick={() => setEditingTiers((prev) => !prev)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {editingTiers ? "Done" : "Edit"}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
              }}
            >
              {(endorsement.tiers ?? []).length === 0 && !editingTiers ? (
                <Box sx={{ px: 1.5, py: 1 }}>
                  <Typography variant="body2" color="text.disabled" fontStyle="italic">
                    No tiers configured.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Header row */}
                  {((endorsement.tiers ?? []).length > 0 || editingTiers) && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: editingTiers ? "0.4fr 1fr 0.8fr 0.8fr 0.7fr 0.3fr" : "0.5fr 1fr 1fr 1fr",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        bgcolor: isDarkMode ? theme.palette.grey[900] : theme.palette.grey[50],
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {(editingTiers
                        ? ["Tier", "Deductible", "Age Start", "Age End", "Reimb. %", ""]
                        : ["Tier", "Deductible", "Age Range", "Reimb. %"]
                      ).map((h) => (
                        <Typography key={h || "actions"} variant="caption" fontWeight={700} color="text.secondary">
                          {h}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {(endorsement.tiers ?? []).map((tier) => (
                    <Box
                      key={tier.id || tier.tier_order}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: editingTiers ? "0.4fr 1fr 0.8fr 0.8fr 0.7fr 0.3fr" : "0.5fr 1fr 1fr 1fr",
                        gap: 0.5,
                        px: 1.5,
                        py: 0.75,
                        alignItems: "center",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        "&:last-child": { borderBottom: editingTiers ? undefined : "none" },
                      }}
                    >
                      <Typography variant="body2">{tier.tier_order}</Typography>
                      <Typography variant="body2">${tier.deductible.toLocaleString()}</Typography>
                      {editingTiers ? (
                        <>
                          <Typography variant="body2">{tier.age_range_start}</Typography>
                          <Typography variant="body2">{tier.age_range_end}</Typography>
                        </>
                      ) : (
                        <Typography variant="body2">
                          {tier.age_range_start}-{tier.age_range_end}
                        </Typography>
                      )}
                      <Typography variant="body2">{(tier.reimbursement_pct * 100).toFixed(0)}%</Typography>
                      {editingTiers && (
                        <IconButton size="small" onClick={() => handleDeleteTier(tier.id)}>
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </>
              )}
              {editingTiers && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "0.4fr 1fr 0.8fr 0.8fr 0.7fr 0.3fr",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    alignItems: "center",
                    borderTop: (endorsement.tiers ?? []).length > 0 ? undefined : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="body2" color="text.disabled">
                    {getNextTierOrder()}
                  </Typography>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="$"
                    value={newTierDeductible}
                    onChange={(e) => setNewTierDeductible(e.target.value)}
                    slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                    sx={{
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    placeholder="Start"
                    value={newTierAgeStart}
                    onChange={(e) => setNewTierAgeStart(e.target.value)}
                    slotProps={{ htmlInput: { step: 1, min: 0 } }}
                    sx={{
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    placeholder="End"
                    value={newTierAgeEnd}
                    onChange={(e) => setNewTierAgeEnd(e.target.value)}
                    slotProps={{ htmlInput: { step: 1, min: 0 } }}
                    sx={{
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    placeholder="%"
                    value={newTierReimbPct}
                    onChange={(e) => setNewTierReimbPct(e.target.value)}
                    slotProps={{ htmlInput: { step: 1, min: 0, max: 100 } }}
                    sx={{
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleAddTier}
                    disabled={
                      !newTierDeductible ||
                      !newTierAgeStart ||
                      !newTierAgeEnd ||
                      !newTierReimbPct ||
                      isNaN(parseFloat(newTierDeductible)) ||
                      isNaN(parseInt(newTierAgeStart, 10)) ||
                      isNaN(parseInt(newTierAgeEnd, 10)) ||
                      isNaN(parseFloat(newTierReimbPct))
                    }
                    sx={{ color: "primary.main" }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Admin change entries */}
        {(endorsement.type === "admin_changes" ||
          (endorsement.admin_change_entries && endorsement.admin_change_entries.length > 0)) && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.75 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
                Change Entries
              </Typography>
              {userCanEdit && onUpdateAdminChangeEntries && (
                <Typography
                  variant="caption"
                  onClick={() => setEditingAdminEntries((prev) => !prev)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {editingAdminEntries ? "Done" : "Edit"}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                p: 1.5,
              }}
            >
              {(endorsement.admin_change_entries ?? []).length === 0 && !editingAdminEntries ? (
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                  No change entries recorded.
                </Typography>
              ) : (
                <>
                  {(endorsement.admin_change_entries ?? []).map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 0.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        "&:last-child": { borderBottom: editingAdminEntries ? undefined : "none" },
                      }}
                    >
                      <Typography variant="body2">{formatToTitleCase(entry.admin_change_type)}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {entry.effective_date}
                        </Typography>
                        {editingAdminEntries && (
                          <IconButton size="small" onClick={() => handleDeleteAdminChangeEntry(entry.id)}>
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))}
                </>
              )}
              {editingAdminEntries && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, pt: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Change type..."
                    value={newAdminChangeType}
                    onChange={(e) => setNewAdminChangeType(e.target.value)}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    value={newAdminChangeDate}
                    onChange={(e) => setNewAdminChangeDate(e.target.value)}
                    sx={{
                      width: 140,
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleAddAdminChangeEntry}
                    disabled={!newAdminChangeType.trim() || !newAdminChangeDate}
                    sx={{ color: "primary.main" }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Amendment entries */}
        {(endorsement.type === "amendments" ||
          (endorsement.amendment_entries && endorsement.amendment_entries.length > 0)) && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2, mb: 0.75 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
                Amendment Entries
              </Typography>
              {userCanEdit && onUpdateAmendmentEntries && (
                <Typography
                  variant="caption"
                  onClick={() => {
                    setEditingAmendmentEntries((prev) => {
                      if (!prev) setNewAmendmentNumber(getNextAmendmentNumber());
                      return !prev;
                    });
                  }}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {editingAmendmentEntries ? "Done" : "Edit"}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: "background.paper",
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                p: 1.5,
              }}
            >
              {(endorsement.amendment_entries ?? []).length === 0 && !editingAmendmentEntries ? (
                <Typography variant="body2" color="text.disabled" fontStyle="italic">
                  No amendment entries recorded.
                </Typography>
              ) : (
                <>
                  {(endorsement.amendment_entries ?? []).map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 0.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        "&:last-child": { borderBottom: editingAmendmentEntries ? undefined : "none" },
                      }}
                    >
                      <Typography variant="body2">Amendment #{entry.amendment_number}</Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {entry.effective_date}
                        </Typography>
                        {editingAmendmentEntries && (
                          <IconButton size="small" onClick={() => handleDeleteAmendmentEntry(entry.id)}>
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))}
                </>
              )}
              {editingAmendmentEntries && (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1, pt: 1 }}>
                  <Tooltip title={isAmendmentNumberDuplicate ? "This number already exists" : ""} arrow>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="#"
                      value={newAmendmentNumber}
                      onChange={(e) => setNewAmendmentNumber(e.target.value)}
                      error={isAmendmentNumberDuplicate}
                      slotProps={{ htmlInput: { step: 1 } }}
                      sx={{
                        width: 70,
                        "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                        "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                      }}
                    />
                  </Tooltip>
                  <TextField
                    size="small"
                    type="date"
                    value={newAmendmentDate}
                    onChange={(e) => setNewAmendmentDate(e.target.value)}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 28, fontSize: "0.8125rem" },
                      "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleAddAmendmentEntry}
                    disabled={
                      newAmendmentNumber === "" ||
                      isNaN(parseInt(newAmendmentNumber, 10)) ||
                      isAmendmentNumberDuplicate ||
                      !newAmendmentDate
                    }
                    sx={{ color: "primary.main" }}
                  >
                    <Add sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Qualifying codes — Workflow 7 (type-gated: always show for deductible_modification) */}
        {(endorsement.type === "deductible_modification" ||
          endorsement.codes?.icd?.length ||
          endorsement.codes?.cpt?.length) && (
          <>
            <SectionLabel>Qualifying Codes</SectionLabel>
            {(endorsement.type === "deductible_modification" ||
              (endorsement.codes?.icd && endorsement.codes.icd.length > 0)) && (
              <EditableCodeChipSection
                label="ICD Codes"
                color="icd"
                codes={endorsement.codes?.icd || []}
                canEdit={userCanEdit && !!onUpdateCodes}
                onUpdate={(codes) => onUpdateCodes?.(endorsementId, "icd", codes)}
                placeholder="e.g. E11.9"
                maxLength={8}
              />
            )}
            {(endorsement.type === "deductible_modification" ||
              (endorsement.codes?.cpt && endorsement.codes.cpt.length > 0)) && (
              <EditableCodeChipSection
                label="CPT Codes"
                color="cpt"
                codes={endorsement.codes?.cpt || []}
                canEdit={userCanEdit && !!onUpdateCodes}
                onUpdate={(codes) => onUpdateCodes?.(endorsementId, "cpt", codes)}
                placeholder="e.g. 99213"
                maxLength={8}
              />
            )}
          </>
        )}

        {/* Workflow 9 — Affected claimants accordion (deductible_modification type) */}
        {isDeductibleMod && (
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              mt: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px !important",
              "&::before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.5 } }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
                <PeopleOutline sx={{ fontSize: 16 }} />
                <Typography variant="body2" fontWeight={600}>
                  Affected Claimants
                </Typography>
                {affectedClaimants.length > 0 && (
                  <Chip label={affectedClaimants.length} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />
                )}
                {userCanEdit && (
                  <Tooltip title="Add claimant modification">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeductibleModDialogOpen(true);
                      }}
                      sx={{ ml: "auto", mr: 0.5 }}
                    >
                      <Add sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
              {affectedClaimants.length === 0 ? (
                <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                  No claimants have been applied to this endorsement yet.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {affectedClaimants.map((mod) => {
                    const claimantName =
                      [mod.claimant_first_name, mod.claimant_last_name].filter(Boolean).join(" ") ||
                      mod.claimant_id_ref;
                    return (
                      <Box
                        key={mod.id}
                        component="button"
                        onClick={() => void navigate(`/claimants/${mod.claimant_id_ref}/show?tab=deductibles`)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          bgcolor: "transparent",
                          cursor: "pointer",
                          textAlign: "left",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {claimantName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatToTitleCase(mod.qualifying_condition)} &bull; {mod.trigger_date || "No trigger date"}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={mod.is_triggered ? "success.main" : "text.disabled"}
                          >
                            {formatDeductibleDollars(mod.modified_deductible)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            from {formatDeductibleDollars(mod.original_deductible)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Deductible modification dialog — add claimant from endorsement context */}
        {isDeductibleMod && (
          <DeductibleModificationDialog
            open={deductibleModDialogOpen}
            onClose={() => setDeductibleModDialogOpen(false)}
            policyId={policyId}
            endorsementId={endorsementId}
            mode="create"
            onSuccess={() => void refetchAffectedClaimants()}
          />
        )}

        {/* Event History — collapsible, powered by audit_logs table */}
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded={false}
          sx={{
            mt: 1,
            bgcolor: "background.paper",
            borderRadius: "12px !important",
            border: `1px solid ${theme.palette.divider}`,
            "&::before": { display: "none" },
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: 40,
              px: 1.5,
              "& .MuiAccordionSummary-content": { my: 0.75 },
            }}
          >
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled" }}>
              Event History
              {auditRecords.length > 0 && (
                <Typography component="span" variant="caption" sx={{ ml: 1, color: "text.disabled", fontWeight: 400 }}>
                  ({auditRecords.length})
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
            {auditLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : auditRecords.length === 0 ? (
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                No events recorded yet.
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {auditRecords.map((record, idx) => (
                  <AuditEventRow
                    key={record.id}
                    record={record}
                    isLast={idx === auditRecords.length - 1}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* PDF split-pane toggle */}
        {onTogglePdf && (
          <Box
            component="button"
            onClick={onTogglePdf}
            sx={{
              mt: 1,
              width: "100%",
              py: 1,
              px: 2,
              border: "1px solid",
              borderColor: pdfOpen ? "primary.main" : "divider",
              borderRadius: 1.5,
              bgcolor: pdfOpen
                ? isDarkMode
                  ? "rgba(25, 118, 210, 0.08)"
                  : "rgba(25, 118, 210, 0.04)"
                : "background.paper",
              fontSize: "0.8125rem",
              color: pdfOpen ? "primary.main" : "text.secondary",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              fontWeight: 500,
              "&:hover": { bgcolor: pdfOpen ? "rgba(25, 118, 210, 0.12)" : "action.hover" },
            }}
          >
            <Description sx={{ fontSize: 14 }} /> {pdfOpen ? "Close PDF" : "View Policy PDF"}
          </Box>
        )}
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Endorsement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this endorsement? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDeleteConfirmOpen(false)}>Cancel</MuiButton>
          <MuiButton
            color="error"
            variant="contained"
            onClick={() => {
              setDeleteConfirmOpen(false);
              onDelete?.(endorsementId);
            }}
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Admin change entry confirmation */}
      <Dialog open={adminChangeConfirmOpen} onClose={() => setAdminChangeConfirmOpen(false)}>
        <DialogTitle>Admin Change Recorded</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The admin change entry has been saved. If this change requires follow-up action, remember to create a task
            so it doesn&apos;t get missed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton variant="contained" onClick={() => setAdminChangeConfirmOpen(false)}>
            Got it
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="overline"
    sx={{ display: "block", fontWeight: 700, letterSpacing: "0.08em", color: "text.disabled", mb: 0.75, mt: 2 }}
  >
    {children}
  </Typography>
);

const BooleanBadge = ({ value }: { value: boolean }) =>
  value ? (
    <Box sx={{ color: "success.dark", display: "flex", alignItems: "center", gap: 0.5 }}>
      <CheckCircle sx={{ fontSize: 14 }} /> Yes
    </Box>
  ) : (
    <Box sx={{ color: "text.disabled", display: "flex", alignItems: "center", gap: 0.5 }}>
      <Cancel sx={{ fontSize: 14 }} /> No
    </Box>
  );

/**
 * Infers the display type for a config value based on the key name and JS type.
 */
const inferConfigType = (key: string, value: unknown): "boolean" | "dollar" | "percent" | "text" => {
  if (typeof value === "boolean") return "boolean";
  // Key-based inference only applies to numeric values — strings like "Fixed" should render as text
  if (typeof value === "number") {
    if (
      key.includes("amount") ||
      key.includes("payment") ||
      key.includes("threshold") ||
      key.includes("limit") ||
      key.includes("benefit") ||
      key.includes("deductible") ||
      key.includes("maximum")
    )
      return "dollar";
    if (key.includes("pct") || key.includes("percentage") || key.includes("rate") || key.includes("ratio"))
      return "percent";
  }
  return "text";
};

const formatConfigValue = (value: unknown, type: string) => {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  if (type === "boolean") return <BooleanBadge value={value as boolean} />;
  if (type === "dollar") return `$${Number(value).toLocaleString()}`;
  if (type === "percent") return `${(Number(value) * 100).toFixed(0)}%`;
  if (type === "number") return Number(value).toLocaleString();
  if (type === "date") return String(value);
  if (type === "select") return formatToTitleCase(String(value));
  return String(value);
};

const ConfigFields = ({
  config,
  endorsementType,
}: {
  config: EndorsementListItem["config"];
  endorsementType: EndorsementType;
}) => {
  if (!config) {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ py: 1.5 }}>
        No configuration — this endorsement type is event-driven only.
      </Typography>
    );
  }

  const entries = Object.entries(config as Record<string, unknown>);
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.disabled" fontStyle="italic" sx={{ py: 1.5 }}>
        No configurable fields for this endorsement type.
      </Typography>
    );
  }

  // Build a lookup from field schemas so we use the authoritative display type
  const schema = getFieldSchema(endorsementType);
  const schemaBySource = new Map(schema?.map((f) => [f.source, f]) ?? []);

  return (
    <>
      {entries.map(([key, val]) => {
        const fieldDef = schemaBySource.get(key);
        const label = fieldDef?.label ?? formatToTitleCase(key);
        const type = fieldDef ? fieldDef.type : inferConfigType(key, val);
        return (
          <Box
            key={key}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ width: "50%", pr: 1.5 }}>
              {label}
            </Typography>
            <Typography variant="body2" component="div" sx={{ display: "flex", alignItems: "center" }}>
              {formatConfigValue(val, type)}
            </Typography>
          </Box>
        );
      })}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Event History — audit log timeline
// ─────────────────────────────────────────────────────────────

/** Dot color for each audit action type */
const EVENT_DOT_COLORS: Record<string, string> = {
  create: "#22c55e", // green
  update: "#3b82f6", // blue
  delete: "#ef4444", // red
  "update:activated": "#22c55e", // green — activation
  "update:deactivated": "#f59e0b", // amber — deactivation
  "update:configuration": "#3b82f6", // blue
  "update:codes": "#8b5cf6", // purple
  "update:qualifying_conditions": "#8b5cf6",
  "update:admin_change_entries": "#3b82f6",
  "update:amendment_entries": "#3b82f6",
  "update:tiers": "#8b5cf6", // purple — tier changes
  "update:excluded_icd": "#8b5cf6",
  "update:excluded_cpt": "#8b5cf6",
  "update:ndc_codes": "#8b5cf6",
  "update:excluded_countries": "#8b5cf6",
  "update:excluded_facilities": "#8b5cf6",
  "update:credited_claim_ids": "#8b5cf6",
  "update:trigger_types": "#8b5cf6",
  "update:phases": "#8b5cf6",
};

/** Formats an ISO datetime to a compact display string */
const formatEventDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/** Human-readable labels for specific update actions */
const UPDATE_ACTION_LABELS: Record<string, string> = {
  "update:activated": "Activated",
  "update:deactivated": "Deactivated",
  "update:configuration": "Updated configuration",
  "update:codes": "Updated qualifying codes",
  "update:qualifying_conditions": "Updated qualifying conditions",
  "update:admin_change_entries": "Updated change entries",
  "update:amendment_entries": "Updated amendment entries",
  "update:tiers": "Updated deductible tiers",
  "update:domestic_reimbursement_entries": "Updated reimbursement entries",
};

/** Builds a human-readable description from an audit record */
const getEventDescription = (record: AuditRecord): string => {
  const action = record.action?.toLowerCase() ?? "";
  if (action === "create") return "Created";
  if (action === "delete") return "Deleted";
  if (UPDATE_ACTION_LABELS[action]) return UPDATE_ACTION_LABELS[action];
  if (action === "update") return "Updated";
  return formatToTitleCase(action || "Unknown");
};

/** UUID pattern — used to hide raw UUIDs from the principal display */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Renders a single row in the event history timeline.
 * Shows a colored dot, action label, principal (user), and formatted timestamp.
 */
const AuditEventRow = ({
  record,
  isLast,
  isDarkMode,
}: {
  record: AuditRecord;
  isLast: boolean;
  isDarkMode: boolean;
}) => {
  const dotColor = EVENT_DOT_COLORS[record.action?.toLowerCase()] ?? "#9ca3af";

  return (
    <Box sx={{ display: "flex", gap: 1.5, position: "relative", pb: isLast ? 0 : 1.5 }}>
      {/* Timeline connector line + dot */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: 12,
          flexShrink: 0,
          pt: 0.5,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: dotColor,
            flexShrink: 0,
          }}
        />
        {!isLast && (
          <Box
            sx={{
              width: 1.5,
              flex: 1,
              bgcolor: isDarkMode ? "grey.700" : "grey.300",
              mt: 0.5,
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.3 }}>
          {getEventDescription(record)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 0.25, flexWrap: "wrap" }}>
          {(record.principal_name || (record.principal && !UUID_REGEX.test(record.principal))) && (
            <Typography variant="caption" color="text.secondary">
              {record.principal_name || record.principal}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            {formatEventDate(record.created_datetime)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
